import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Button, Spinner, Modal} from "react-bootstrap";
import Layout from "../../../layouts/default";
import { getTodayCampaigns, updateCampaignStatus } from "../../../store/slices/campaigns/campaigns";
import RunReportFilterForm from "./RunReportFilterForm";
import { getCampaignSuggestions } from "../../../store/slices/campaigns/campaign";
import { useNavigate } from "react-router-dom";
import { ExclamationTriangle, Eye, Trash, FileArrowDown } from "react-bootstrap-icons";
import { DateTime } from "luxon";
import SmartPagination from "./DynamicSmartPagination";
import JsonModal from "./JsonModel";
import { toast } from "react-toastify";
import { hasPermission } from "../../../services/utils";
import { PERMISSIONS } from "../../../contants/permission";
import { downloadCampaignContactsCsv } from "../../../services/exportFile/downloadCampaignContacts";

const CampaignRunReportDetailsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [reason, setReason] = useState("");
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showModel, setShowModel] = useState(false);
  const [selectedPayload, setSelectedPayload] = useState(null);
  const recordsPerPage = 10;
  // [CAMPAIGN_CONTACTS_EXPORT] id of the run whose contacts CSV is currently being generated
  const [downloadingId, setDownloadingId] = useState(null);

  // [CAMPAIGN_CONTACTS_EXPORT] export this Email run's contacted mobile numbers as CSV
  const handleDownloadContacts = async (log) => {
    try {
      setDownloadingId(log?._id);
      await downloadCampaignContactsCsv({
        dispatch,
        channel: "email",
        campaignId: log?.campaignId,
        campaignUuid: log?.campaignUuid,
      });
    } catch (err) {
      console.log("Error in handleDownloadContacts:", err);
      toast.error(err?.message || "Failed to download contacts CSV.");
    } finally {
      setDownloadingId(null);
    }
  };

  const { todayCampaigns } = useSelector((store) => store.campaignsSlice);
  const currentPageData = todayCampaigns?.data?.result || [];
  const totalCount = todayCampaigns?.data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / recordsPerPage);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await dispatch(
        getTodayCampaigns({
          filterData: {
            ...filters,
            limit: recordsPerPage,
            page,
          },
        })
      );
      setLoading(false);
    };

    fetchData();
  }, [dispatch, filters, page]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const loadCampaignOptions = async (inputValue) => {
    if (inputValue.length < 4) return [];

    try {
      const response = await dispatch(
        getCampaignSuggestions({ search: inputValue })
      ).unwrap();

      if (!Array.isArray(response)) return [];

      return response
        .filter(camp =>
          camp?.campaignName?.toLowerCase().includes(inputValue.toLowerCase()) &&
          camp?.channelName === 'email'
        )
        .map(item => ({
          label: item.campaignName,
          value: item.campaignId,
        }));

    } catch (error) {
      console.error("Error loading campaign options:", error);
      return [];
    }
  };

  const handleViewCampaign = (campaignUuid, status) => {
    if (!campaignUuid) {
      alert(`Campaign is ${status}`);
    } else {
      navigate(`${campaignUuid}`);
    }
  };

  const handleMarkDelete = async (data) => {
    if (!window.confirm("Are You sure you want to delete this campaign from here.")) {
      return;
    }

    const id = data._id;
    const user = JSON.parse(localStorage.getItem("user"));
    const userId = user?.data?.id;

    if (!id || !userId) {
      toast.error("Something went wrong. id or userId is missing...");
      return;
    }

    const payload = { id, userId, status: 'deleted' };
    try {
      await dispatch(updateCampaignStatus(payload)).unwrap();
      await dispatch(
        getTodayCampaigns({
          filterData: {
            ...filters,
            limit: recordsPerPage,
            page,
          },
        })
      );
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete campaign");
    }
  };


  const showFailedJson = (payload) => {
    setSelectedPayload(payload);
    setShowModel(true);
  };

  const closeModal = () => {
    setSelectedPayload(null);
    setShowModel(false);
  };

  const handleUpdateStatus = async () => {
    if (!selectedStatus) {
      toast.error("Please select a status");
      return;
    }

    if (selectedStatus === "failed" && !reason.trim()) {
      toast.error("Please enter a failure reason");
      return;
    }

    const user = JSON.parse(localStorage.getItem("user"));
    const userId = user?.data?.id;

    if (!selectedCampaignId || !userId) {
      toast.error("Missing required data.");
      return;
    }

    try {
      const payload = {
        id: selectedCampaignId,
        status: selectedStatus,
        failedReason: selectedStatus === "failed" ? reason : null,
        userId,
      };

      await dispatch(updateCampaignStatus(payload)).unwrap();

      setOpenUpdateModal(false);
      setSelectedStatus("");
      setReason("");

      // Reload data
      await dispatch(
        getTodayCampaigns({
          filterData: {
            ...filters,
            limit: recordsPerPage,
            page,
          },
        })
      );

    } catch (err) {
      console.error(err);
      toast.error("Failed to update campaign status");
    }
  };


  return (
    <Layout>
      <div>
        <h3 className="mt-3 mb-3">Email Campaign Run Report</h3>

        <RunReportFilterForm
          loadCampaignOptions={loadCampaignOptions}
          setFilterData={setFilters}
        />

        <Table striped bordered hover>
          <thead>
            <tr>
              <th>S.No.</th>
              <th>Campaign Name</th>
              <th>CampaignId</th>
              <th>Scheduled Date Time</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" className="text-center py-4">
                  <Spinner animation="border" variant="primary" role="status" />
                  <div className="mt-2">Loading campaigns...</div>
                </td>
              </tr>
            ) : currentPageData.length > 0 ? (
              currentPageData.map((log, index) => {
                const status = (log?.status || "").toLowerCase();
                const statusColor =
                  status === "pending"
                    ? "orange"
                    : status === "completed"
                      ? "green"
                      : status === "started"
                        ? "gray"
                        : "red";

                return (
                  <tr key={log.campaignUuid || index}>
                    <td>{(page - 1) * recordsPerPage + index + 1}</td>
                    <td>{log?.campaignName || "NA"}</td>
                    <td>{log?.campaignId || "NA"}</td>
                    <td>
                      {(() => {
                        const rawDate = log?.scheduledTime;
                        if (!rawDate) return "NA";

                        let dt = DateTime.fromISO(rawDate);
                        if (!dt.isValid) {
                          dt = DateTime.fromFormat(rawDate, "yyyy-MM-dd H:m:s");
                        }

                        return dt.isValid
                          ? dt.toFormat("yyyy-MM-dd HH:mm:ss")
                          : "Invalid Date";
                      })()}
                    </td>
                    <td style={{ color: statusColor }}>
                      {log?.status || "NA"}{" "}
                      {log?.status === "failed" && (
                        <ExclamationTriangle
                          onClick={() => showFailedJson(log)}
                          color="red"
                          size={15}
                          style={{ cursor: "pointer" }}
                        />
                      )}
                    </td>
                    <td>
                      {
                        hasPermission(PERMISSIONS.EMAIL_CAMPAIGN_REPORT.VIEW) && <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() =>
                            handleViewCampaign(log?.campaignUuid, log?.status)
                          }
                        >
                          <Eye title="View Report" size={16} />
                        </Button>
                      }
                      {
                        log?.status === 'pending' && hasPermission(PERMISSIONS.EMAIL_CAMPAIGN_REPORT.DELETE) && <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleMarkDelete(log)}
                        >
                          <Trash size={16} />
                        </Button>
                      }
                      {
                        log?.status === 'started' && <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            setOpenUpdateModal(true);
                            setSelectedCampaignId(log._id);
                          }}
                        >
                          Update Status
                        </Button>
                      }
                      {/* [CAMPAIGN_CONTACTS_EXPORT] Download CSV — shown ONLY for
                          "completed" runs; exports the contacted mobile numbers */}
                      {
                        log?.status === 'completed' && hasPermission(PERMISSIONS.EMAIL_CAMPAIGN_REPORT.DOWNLOAD) && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="ms-2"
                            title="Download contacted numbers CSV"
                            disabled={downloadingId === log._id}
                            onClick={() => handleDownloadContacts(log)}
                          >
                            {downloadingId === log._id ? (
                              <Spinner size="sm" animation="border" />
                            ) : (
                              <FileArrowDown size={16} />
                            )}
                          </Button>
                        )
                      }
                    </td>

                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="10" className="text-center py-4">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        <Modal show={openUpdateModal} onHide={() => setOpenUpdateModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Update Campaign Status</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">Select Status</label>
              <select
                className="form-select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="">-- Select --</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            {selectedStatus === "failed" && (
              <div className="mb-3">
                <label className="form-label">Failure Reason</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter failure reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setOpenUpdateModal(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={handleUpdateStatus}>
              Update
            </Button>
          </Modal.Footer>
        </Modal>


        <JsonModal
          show={showModel}
          onClose={closeModal}
          payload={selectedPayload}
        />

        {totalPages > 1 && (
          <SmartPagination
            totalPages={totalPages}
            currentPage={page}
            onPageChange={setPage}
          />
        )}
      </div>
    </Layout>
  );
};

export default CampaignRunReportDetailsPage;
